import React, { useCallback, useEffect, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import './ChannelManagementPage.css';

const API_BASE = process.env.REACT_APP_API_BASE_URL || '';

export default function ChannelManagementPage() {
  const { currentUser } = useAuth();

  const [publications, setPublications] = useState([]);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saveMsg, setSaveMsg] = useState(null);

  const [testInput, setTestInput] = useState('');
  const [testRunning, setTestRunning] = useState(false);
  const [testResults, setTestResults] = useState(null);

  const [backfillLimit, setBackfillLimit] = useState(2);
  const [backfillRunning, setBackfillRunning] = useState(false);
  const [backfillResults, setBackfillResults] = useState(null);

  const [inspectInput, setInspectInput] = useState('');
  const [inspectRunning, setInspectRunning] = useState(false);
  const [inspectResult, setInspectResult] = useState(null);

  const authedFetch = useCallback(async (url, options = {}) => {
    const token = await currentUser.getIdToken();
    const resolvedUrl = url.startsWith('http') ? url : `${API_BASE}${url}`;
    return fetch(resolvedUrl, {
      ...options,
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
        ...(options.headers || {}),
      },
    });
  }, [currentUser]);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      setLoading(true);
      try {
        const [pubRes, cfgRes] = await Promise.all([
          authedFetch('/api/v1/admin/channel-management/publications'),
          authedFetch('/api/v1/admin/channel-management/config'),
        ]);
        const pubData = await pubRes.json();
        const cfgData = await cfgRes.json();
        if (cancelled) return;
        if (!pubData.success) {
          setSaveMsg({ type: 'error', text: pubData.message || 'Failed to load publications.' });
        }
        setPublications(pubData.publications || []);
        setSelectedIds(new Set(cfgData.config?.enabledPublicationIds || []));
      } catch (err) {
        if (!cancelled) setSaveMsg({ type: 'error', text: err.message });
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    load();
    return () => { cancelled = true; };
  }, [authedFetch]);

  function togglePub(id) {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  async function saveConfig() {
    setSaving(true);
    setSaveMsg(null);
    try {
      const res = await authedFetch('/api/v1/admin/channel-management/config', {
        method: 'POST',
        body: JSON.stringify({ enabledPublicationIds: [...selectedIds] }),
      });
      const data = await res.json();
      setSaveMsg(data.success
        ? { type: 'success', text: 'Channel config saved.' }
        : { type: 'error', text: data.message || 'Save failed.' });
    } catch (err) {
      setSaveMsg({ type: 'error', text: err.message });
    } finally {
      setSaving(false);
    }
  }

  async function runTestApply() {
    const ids = testInput.split(',').map((s) => s.trim()).filter(Boolean);
    if (ids.length === 0 || ids.length > 5) {
      setTestResults({ error: 'Enter 1-5 variant IDs, comma-separated.' });
      return;
    }
    setTestRunning(true);
    setTestResults(null);
    try {
      const res = await authedFetch('/api/v1/admin/channel-management/test-apply', {
        method: 'POST',
        body: JSON.stringify({ variantIds: ids }),
      });
      const data = await res.json();
      setTestResults(data);
    } catch (err) {
      setTestResults({ error: err.message });
    } finally {
      setTestRunning(false);
    }
  }

  async function runBackfill(dryRun, resetTracking) {
    setBackfillRunning(true);
    setBackfillResults(null);
    try {
      const res = await authedFetch('/api/v1/admin/channel-management/backfill', {
        method: 'POST',
        body: JSON.stringify({ limit: backfillLimit, dryRun, resetTracking }),
      });
      const data = await res.json();
      setBackfillResults(data);
    } catch (err) {
      setBackfillResults({ error: err.message });
    } finally {
      setBackfillRunning(false);
    }
  }

  async function runInspect() {
    const vid = inspectInput.trim();
    if (!vid) return;
    setInspectRunning(true);
    setInspectResult(null);
    try {
      const res = await authedFetch(`/api/v1/admin/channel-management/variant-publications/${encodeURIComponent(vid)}`);
      const data = await res.json();
      setInspectResult(data);
    } catch (err) {
      setInspectResult({ error: err.message });
    } finally {
      setInspectRunning(false);
    }
  }

  if (loading) {
    return <div className="channel-mgmt-page"><div className="channel-mgmt-loading">Loading channels...</div></div>;
  }

  return (
    <div className="channel-mgmt-page">
      <div className="channel-mgmt-header">
        <h1>Channel Management</h1>
        <p>Control which sales channels fractional variants are published to.</p>
      </div>

      {/* Channel Selection */}
      <div className="channel-mgmt-section">
        <h2>Sales Channels</h2>
        {publications.length === 0 ? (
          <div className="channel-mgmt-alert channel-mgmt-alert-info">
            No publications found. Ensure your Shopify app has <code>read_publications</code> scope.
          </div>
        ) : (
          <ul className="channel-mgmt-pub-list">
            {publications.map((pub) => (
              <li key={pub.id} className="channel-mgmt-pub-item">
                <label>
                  <input
                    type="checkbox"
                    checked={selectedIds.has(pub.id)}
                    onChange={() => togglePub(pub.id)}
                  />
                  <span>
                    <span className="channel-mgmt-pub-name">{pub.name}</span>
                    {pub.appTitle && <span className="channel-mgmt-pub-app"> — {pub.appTitle}</span>}
                  </span>
                </label>
              </li>
            ))}
          </ul>
        )}
        <div className="channel-mgmt-btn-row">
          <button
            className="channel-mgmt-btn channel-mgmt-btn-primary"
            onClick={saveConfig}
            disabled={saving}
          >
            {saving ? 'Saving...' : 'Save Config'}
          </button>
          <span style={{ color: '#6b7280', fontSize: 13 }}>
            {selectedIds.size} of {publications.length} channels selected
          </span>
        </div>
        {saveMsg && (
          <div className={`channel-mgmt-alert channel-mgmt-alert-${saveMsg.type}`}>
            {saveMsg.text}
          </div>
        )}
      </div>

      {/* Test Apply */}
      <div className="channel-mgmt-section">
        <h2>Test Apply</h2>
        <p style={{ color: '#6b7280', margin: '0 0 12px', fontSize: 14 }}>
          Apply saved channel config to specific variant IDs for testing.
        </p>
        <div className="channel-mgmt-btn-row">
          <input
            className="channel-mgmt-input channel-mgmt-input-wide"
            placeholder="Variant IDs (comma-separated, e.g. 12345, 67890)"
            value={testInput}
            onChange={(e) => setTestInput(e.target.value)}
          />
          <button
            className="channel-mgmt-btn channel-mgmt-btn-primary"
            onClick={runTestApply}
            disabled={testRunning}
          >
            {testRunning ? 'Applying...' : 'Test Apply'}
          </button>
        </div>
        {testResults && (
          testResults.error ? (
            <div className="channel-mgmt-alert channel-mgmt-alert-error">{testResults.error || testResults.message}</div>
          ) : (
            <table className="channel-mgmt-table">
              <thead>
                <tr><th>Variant ID</th><th>Status</th><th>Message</th></tr>
              </thead>
              <tbody>
                {(testResults.results || []).map((r, i) => (
                  <tr key={i}>
                    <td>{r.variantId}</td>
                    <td className={r.status === 'ok' ? 'channel-mgmt-status-ok' : 'channel-mgmt-status-error'}>{r.status}</td>
                    <td>{r.message || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )
        )}
      </div>

      {/* Backfill */}
      <div className="channel-mgmt-section">
        <h2>Backfill Existing Variants</h2>
        <p style={{ color: '#6b7280', margin: '0 0 12px', fontSize: 14 }}>
          Apply channel config to existing fractional variants (SKUs ending in e.g. -2.5M).
        </p>
        <div className="channel-mgmt-btn-row">
          <label style={{ fontSize: 14, color: '#374151' }}>
            Limit:
            <input
              type="number"
              className="channel-mgmt-input channel-mgmt-input-narrow"
              style={{ marginLeft: 6 }}
              min={1}
              max={100}
              value={backfillLimit}
              onChange={(e) => setBackfillLimit(parseInt(e.target.value, 10) || 2)}
            />
          </label>
          <button
            className="channel-mgmt-btn channel-mgmt-btn-secondary"
            onClick={() => runBackfill(true, false)}
            disabled={backfillRunning}
          >
            Dry Run
          </button>
          <button
            className="channel-mgmt-btn channel-mgmt-btn-danger"
            onClick={() => runBackfill(false, false)}
            disabled={backfillRunning}
          >
            {backfillRunning ? 'Running...' : 'Execute Backfill'}
          </button>
          <button
            className="channel-mgmt-btn channel-mgmt-btn-secondary"
            onClick={() => runBackfill(false, true)}
            disabled={backfillRunning}
            title="Clear tracking history and re-process all fractional variants"
          >
            Reset &amp; Re-run
          </button>
        </div>
        {backfillResults && (
          backfillResults.error ? (
            <div className="channel-mgmt-alert channel-mgmt-alert-error">{backfillResults.error || backfillResults.message}</div>
          ) : (
            <>
              <div className="channel-mgmt-alert channel-mgmt-alert-info">
                {backfillResults.dryRun ? 'Dry run — ' : 'Done — '}
                {backfillResults.dryRun
                  ? `${backfillResults.candidates?.length || 0} pending variants found`
                  : `${backfillResults.total || 0} variants processed, ${backfillResults.remaining ?? '?'} remaining`
                }
                {backfillResults.alreadyProcessed > 0 && ` (${backfillResults.alreadyProcessed} already backfilled, skipped)`}
              </div>
              <table className="channel-mgmt-table">
                <thead>
                  <tr><th>Variant ID</th><th>SKU</th><th>Status</th><th>Message</th></tr>
                </thead>
                <tbody>
                  {(backfillResults.candidates || backfillResults.results || []).map((r, i) => (
                    <tr key={i}>
                      <td>{r.variantId}</td>
                      <td>{r.sku}</td>
                      <td className={
                        r.status === 'ok' ? 'channel-mgmt-status-ok'
                          : r.status === 'error' ? 'channel-mgmt-status-error'
                          : ''
                      }>{r.status || 'candidate'}</td>
                      <td>{r.message || '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </>
          )
        )}
      </div>

      {/* Variant Inspector */}
      <div className="channel-mgmt-section">
        <h2>Variant Inspector</h2>
        <p style={{ color: '#6b7280', margin: '0 0 12px', fontSize: 14 }}>
          Check which channels a specific variant is currently published to.
        </p>
        <div className="channel-mgmt-btn-row">
          <input
            className="channel-mgmt-input channel-mgmt-input-wide"
            placeholder="Variant ID (numeric or GID)"
            value={inspectInput}
            onChange={(e) => setInspectInput(e.target.value)}
          />
          <button
            className="channel-mgmt-btn channel-mgmt-btn-primary"
            onClick={runInspect}
            disabled={inspectRunning}
          >
            {inspectRunning ? 'Checking...' : 'Inspect'}
          </button>
        </div>
        {inspectResult && (
          inspectResult.error ? (
            <div className="channel-mgmt-alert channel-mgmt-alert-error">{inspectResult.error || inspectResult.message}</div>
          ) : (
            <>
              {inspectResult.sku && (
                <div className="channel-mgmt-alert channel-mgmt-alert-info" style={{ marginBottom: 8 }}>
                  SKU: <strong>{inspectResult.sku}</strong>
                </div>
              )}
              <table className="channel-mgmt-table">
                <thead>
                  <tr><th>Channel</th><th>Publication ID</th><th>Status</th></tr>
                </thead>
                <tbody>
                  {(inspectResult.publications || []).map((p, i) => (
                    <tr key={i}>
                      <td>{p.name}</td>
                      <td style={{ fontSize: 12, color: '#6b7280' }}>{p.publicationId}</td>
                      <td>
                        <span className={`channel-mgmt-pub-badge ${p.isPublished ? 'channel-mgmt-pub-badge-published' : 'channel-mgmt-pub-badge-unpublished'}`}>
                          {p.isPublished ? 'Published' : 'Unpublished'}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </>
          )
        )}
      </div>
    </div>
  );
}
